/**
 * Mission Customer Modal
 *
 * Sci-Fi HUD styled customer details modal for Order Mission Control.
 * Displays comprehensive customer information including:
 * - Basic profile with VIP badges
 * - Order statistics (total orders, lifetime value)
 * - Loyalty program status (Dee Rewards tier and points)
 * - Payment methods used
 * - Recent orders with clickable navigation
 *
 * @module OrderMissionControl/MissionCustomerModal
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  keyframes,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as DateIcon,
  ShoppingCart as OrdersIcon,
  AttachMoney as MoneyIcon,
  Stars as LoyaltyIcon,
  Payment as PaymentIcon,
  CreditCard as CardIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import config from '../../../config';
import { getStoredToken } from '../../../services/tokenRefreshService';
import { useMissionTheme, type MissionColorTokens } from '../theme';
import GlassCard, { GlassInfoRow, GlassDivider } from './GlassCard';

// ============================================================================
// ANIMATIONS
// ============================================================================

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 10px currentColor; }
  50% { box-shadow: 0 0 25px currentColor, 0 0 50px currentColor; }
`;

const scanLine = keyframes`
  0% { top: 0; opacity: 1; }
  100% { top: 100%; opacity: 0; }
`;

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface CustomerDetails {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  totalOrders: number;
  totalOrderValue: number;
  lastOrderAt: string | null;
  createdAt: string;
  /** Dee Rewards from the native engine; null when it could not be loaded. */
  loyalty: {
    tier: string | null;
    points: number;
  } | null;
  recentOrders: Array<{
    id: string;
    orderNumber: string;
    totalPrice: number;
    status: string;
    createdAt: string;
    paymentMethod: string | null;
  }>;
  paymentMethodsUsed: string[];
}

interface MissionCustomerModalProps {
  open: boolean;
  customerId: string;
  onClose: () => void;
  /** Optional: Pre-populate customer name while loading */
  customerName?: string;
}

// ============================================================================
// HELPERS
// ============================================================================

const formatCurrency = (amount: number): string => {
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  return `R${safeAmount.toFixed(2)}`;
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Invalid date';
  return date.toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusColor = (status: string, mc: MissionColorTokens) => {
  if (!status) return { bg: mc.surface, color: mc.textMuted };
  switch (status.toLowerCase()) {
    case 'completed':
    case 'delivered':
      return { bg: `${mc.success}20`, color: mc.success };
    case 'processing':
    case 'picking':
    case 'packed':
      return { bg: `${mc.info}20`, color: mc.info };
    case 'dispatched':
    case 'tracking':
      return { bg: `${mc.secondary}20`, color: mc.secondary };
    case 'cancelled':
    case 'returned':
      return { bg: `${mc.error}20`, color: mc.error };
    case 'placed':
      return { bg: `${mc.warning}20`, color: mc.warning };
    default:
      return { bg: mc.surface, color: mc.textMuted };
  }
};

const getPaymentInfo = (method: string | null, mc: MissionColorTokens) => {
  if (!method) return { label: 'Unknown', color: mc.textMuted };
  const labels: Record<string, { label: string; color: string }> = {
    paygate: { label: 'Paygate', color: mc.secondary },
    ozow: { label: 'Ozow', color: mc.info },
    snapscan: { label: 'SnapScan', color: mc.warning },
    zapper: { label: 'Zapper', color: mc.gold },
    payflex: { label: 'Payflex', color: mc.success },
    payjustnow: { label: 'PayJustNow', color: mc.error },
  };
  return labels[method.toLowerCase()] || { label: method, color: mc.textMuted };
};

const getCustomerTier = (totalOrders: number, totalValue: number, mc: MissionColorTokens): { tier: string; color: string; icon: string } => {
  if (totalOrders >= 50 || totalValue >= 50000) {
    return { tier: 'VIP', color: mc.gold, icon: '👑' };
  }
  if (totalOrders >= 20 || totalValue >= 20000) {
    return { tier: 'ELITE', color: mc.accent, icon: '💎' };
  }
  if (totalOrders >= 10 || totalValue >= 10000) {
    return { tier: 'LOYAL', color: mc.secondary, icon: '⭐' };
  }
  if (totalOrders >= 3 || totalValue >= 3000) {
    return { tier: 'REGULAR', color: mc.success, icon: '✓' };
  }
  return { tier: 'NEW', color: mc.info, icon: '🆕' };
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function MissionCustomerModal({
  open,
  customerId,
  onClose,
  customerName,
}: MissionCustomerModalProps) {
  const { mc } = useMissionTheme();
  const [customer, setCustomer] = useState<CustomerDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const notify = useNotify();
  const navigate = useNavigate();

  useEffect(() => {
    if (open && customerId) {
      fetchCustomerDetails();
    }
  }, [open, customerId]);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getStoredToken();

      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const response = await fetch(`${config.API_URL}/admin/users/customers/${customerId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) throw new Error('Session expired. Please log in again.');
        if (response.status === 403) throw new Error('You do not have permission to view customer details.');
        if (response.status === 404) throw new Error('Customer not found.');
        throw new Error('Failed to fetch customer details');
      }

      const data = await response.json();
      setCustomer(data.customer);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load customer details';
      setError(errorMessage);
      notify(errorMessage, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const customerTier = customer
    ? getCustomerTier(customer.totalOrders, customer.totalOrderValue, mc)
    : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: mc.background,
          border: `1px solid ${mc.border}`,
          borderRadius: 3,
          backgroundImage: 'none',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, ${mc.secondary}, ${mc.accent}, ${mc.secondary})`,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: mc.elevated,
          color: mc.textPrimary,
          borderBottom: `1px solid ${mc.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              bgcolor: mc.secondary,
              color: mc.background,
              width: 48,
              height: 48,
              border: `2px solid ${mc.secondary}`,
              boxShadow: `0 0 15px ${mc.secondaryGlow}`,
            }}
          >
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 1 }}>
              CUSTOMER INTEL
            </Typography>
            {customerName && !customer && (
              <Typography variant="body2" sx={{ color: mc.textSecondary }}>
                {customerName}
              </Typography>
            )}
          </Box>
        </Stack>
        <IconButton onClick={onClose} sx={{ color: mc.textMuted }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: mc.background }}>
        {loading && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
            <Box sx={{ position: 'relative' }}>
              <CircularProgress
                size={60}
                thickness={2}
                sx={{ color: mc.secondary }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <PersonIcon sx={{ color: mc.textMuted, fontSize: 24 }} />
              </Box>
            </Box>
            <Typography variant="body2" sx={{ mt: 2, color: mc.textMuted, letterSpacing: 2 }}>
              SCANNING DATABASE...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{
              bgcolor: `${mc.error}15`,
              color: mc.error,
              border: `1px solid ${mc.error}40`,
              '& .MuiAlert-icon': { color: mc.error },
            }}
          >
            {error}
          </Alert>
        )}

        {customer && !loading && (
          <Stack spacing={3}>
            {/* Customer Header with VIP Badge */}
            <Box
              sx={{
                p: 3,
                background: mc.glass,
                backdropFilter: 'blur(12px)',
                borderRadius: 3,
                border: `1px solid ${customerTier?.color}40`,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Scan line effect */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  height: 2,
                  bgcolor: customerTier?.color,
                  animation: `${scanLine} 3s ease-out infinite`,
                  boxShadow: `0 0 10px ${customerTier?.color}`,
                }}
              />

              <Stack direction="row" spacing={3} alignItems="center">
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: mc.surface,
                    border: `3px solid ${customerTier?.color}`,
                    boxShadow: `0 0 20px ${customerTier?.color}50`,
                    fontSize: '2rem',
                  }}
                >
                  {customer.firstName?.[0]?.toUpperCase() || '?'}
                </Avatar>

                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
                    <Typography
                      variant="h5"
                      sx={{ color: mc.textPrimary, fontWeight: 700 }}
                    >
                      {customer.firstName} {customer.lastName}
                    </Typography>
                    {customerTier && (
                      <Chip
                        label={`${customerTier.icon} ${customerTier.tier}`}
                        size="small"
                        sx={{
                          bgcolor: `${customerTier.color}20`,
                          color: customerTier.color,
                          border: `1px solid ${customerTier.color}`,
                          fontWeight: 700,
                          letterSpacing: 1,
                          animation: `${pulseGlow} 3s ease-in-out infinite`,
                        }}
                      />
                    )}
                  </Stack>

                  <Stack direction="row" spacing={3}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <EmailIcon sx={{ fontSize: 16, color: mc.textMuted }} />
                      <Typography variant="body2" sx={{ color: mc.textSecondary }}>
                        {customer.email}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <PhoneIcon sx={{ fontSize: 16, color: mc.success }} />
                      <Typography variant="body2" sx={{ color: mc.success }}>
                        {customer.phoneNumber}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack direction="row" spacing={2} mt={1}>
                    <Typography variant="caption" sx={{ color: mc.textMuted }}>
                      <DateIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                      Joined: {formatDate(customer.createdAt)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: mc.textMuted }}>
                      Last Order: {formatDate(customer.lastOrderAt)}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Box>

            {/* Stats Grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
              {/* Total Orders */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: mc.surface,
                  borderRadius: 2,
                  border: `1px solid ${mc.border}`,
                  textAlign: 'center',
                }}
              >
                <OrdersIcon sx={{ fontSize: 32, color: mc.secondary, mb: 1 }} />
                <Typography
                  variant="h4"
                  sx={{ color: mc.textPrimary, fontFamily: 'monospace', fontWeight: 700 }}
                >
                  {customer.totalOrders}
                </Typography>
                <Typography variant="caption" sx={{ color: mc.textMuted }}>
                  TOTAL ORDERS
                </Typography>
              </Box>

              {/* Lifetime Value */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: mc.surface,
                  borderRadius: 2,
                  border: `1px solid ${mc.border}`,
                  textAlign: 'center',
                }}
              >
                <MoneyIcon sx={{ fontSize: 32, color: mc.gold, mb: 1 }} />
                <Typography
                  variant="h4"
                  sx={{ color: mc.gold, fontFamily: 'monospace', fontWeight: 700 }}
                >
                  {formatCurrency(customer.totalOrderValue)}
                </Typography>
                <Typography variant="caption" sx={{ color: mc.textMuted }}>
                  LIFETIME VALUE
                </Typography>
              </Box>

              {/* Loyalty Tier */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: mc.surface,
                  borderRadius: 2,
                  border: `1px solid ${mc.border}`,
                  textAlign: 'center',
                }}
              >
                <LoyaltyIcon sx={{ fontSize: 32, color: mc.warning, mb: 1 }} />
                {customer.loyalty?.tier ? (
                  <Chip
                    label={customer.loyalty.tier}
                    size="small"
                    sx={{
                      bgcolor: `${mc.warning}20`,
                      color: mc.warning,
                      border: `1px solid ${mc.warning}`,
                      fontWeight: 600,
                    }}
                  />
                ) : (
                  <Typography variant="body2" sx={{ color: mc.textMuted }}>
                    No tier
                  </Typography>
                )}
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: mc.textMuted }}>
                  LOYALTY TIER
                </Typography>
              </Box>

              {/* Points */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: mc.surface,
                  borderRadius: 2,
                  border: `1px solid ${mc.border}`,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontSize: 32, mb: 1 }}>🎯</Typography>
                <Typography
                  variant="h4"
                  sx={{ color: mc.textPrimary, fontFamily: 'monospace', fontWeight: 700 }}
                >
                  {customer.loyalty?.points ?? '—'}
                </Typography>
                <Typography variant="caption" sx={{ color: mc.textMuted }}>
                  POINTS
                </Typography>
              </Box>
            </Box>

            {/* Payment Methods */}
            <GlassCard title="Payment Methods" icon={<PaymentIcon />} iconColor={mc.secondary}>
              {customer.paymentMethodsUsed && customer.paymentMethodsUsed.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {customer.paymentMethodsUsed.map((method) => {
                    const info = getPaymentInfo(method, mc);
                    return (
                      <Chip
                        key={method}
                        icon={<CardIcon sx={{ color: `${info.color} !important` }} />}
                        label={info.label}
                        size="small"
                        sx={{
                          bgcolor: `${info.color}15`,
                          color: info.color,
                          border: `1px solid ${info.color}40`,
                        }}
                      />
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ color: mc.textMuted }}>
                  No payment methods recorded
                </Typography>
              )}
            </GlassCard>

            {/* Recent Orders */}
            <GlassCard title="Recent Missions" icon={<OrdersIcon />} iconColor={mc.success}>
              {customer.recentOrders.length > 0 ? (
                <TableContainer
                  sx={{
                    '& .MuiTableCell-root': {
                      borderColor: mc.border,
                      color: mc.textSecondary,
                      py: 1.5,
                    },
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: `${mc.textMuted} !important`, fontWeight: 600 }}>
                          ORDER #
                        </TableCell>
                        <TableCell sx={{ color: `${mc.textMuted} !important`, fontWeight: 600 }}>
                          DATE
                        </TableCell>
                        <TableCell align="right" sx={{ color: `${mc.textMuted} !important`, fontWeight: 600 }}>
                          TOTAL
                        </TableCell>
                        <TableCell sx={{ color: `${mc.textMuted} !important`, fontWeight: 600 }}>
                          PAYMENT
                        </TableCell>
                        <TableCell sx={{ color: `${mc.textMuted} !important`, fontWeight: 600 }}>
                          STATUS
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {customer.recentOrders.map((order) => {
                        const paymentInfo = getPaymentInfo(order.paymentMethod, mc);
                        const statusColors = getStatusColor(order.status, mc);
                        return (
                          <TableRow
                            key={order.id}
                            hover
                            onClick={() => {
                              onClose();
                              navigate(`/orders/${order.id}`);
                            }}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: `${mc.secondary}10 !important`,
                              },
                            }}
                          >
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: 'monospace', color: mc.textPrimary }}
                                >
                                  {order.orderNumber}
                                </Typography>
                                <OpenInNewIcon sx={{ fontSize: 14, color: mc.secondary }} />
                              </Stack>
                            </TableCell>
                            <TableCell>{formatDate(order.createdAt)}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', color: `${mc.gold} !important` }}>
                              {formatCurrency(order.totalPrice)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={paymentInfo.label}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  bgcolor: `${paymentInfo.color}15`,
                                  color: paymentInfo.color,
                                  border: `1px solid ${paymentInfo.color}40`,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={order.status?.toUpperCase()}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  bgcolor: statusColors.bg,
                                  color: statusColors.color,
                                  border: `1px solid ${statusColors.color}40`,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" sx={{ color: mc.textMuted, textAlign: 'center', py: 2 }}>
                  No recent orders found
                </Typography>
              )}
            </GlassCard>
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          bgcolor: mc.elevated,
          borderTop: `1px solid ${mc.border}`,
        }}
      >
        <Typography
          variant="caption"
          sx={{ flex: 1, color: mc.textMuted, fontFamily: 'monospace' }}
        >
          ID: {customerId?.slice(0, 8)}...
        </Typography>
        <Box
          onClick={onClose}
          sx={{
            px: 3,
            py: 1,
            bgcolor: mc.surface,
            borderRadius: 2,
            border: `1px solid ${mc.border}`,
            cursor: 'pointer',
            color: mc.textSecondary,
            '&:hover': {
              bgcolor: mc.hover,
              borderColor: mc.secondary,
            },
          }}
        >
          <Typography variant="button">CLOSE</Typography>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
